import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Bot, Send, Sparkles, User, Crown, Loader2, Trash2 } from "lucide-react";

const SUGGESTIONS = [
  "Which course should I choose after Class 12?",
  "Is JEE or NEET better for me?",
  "Which career has the best growth in 2026?",
  "How do I choose between branch and brand?",
];

export default function ChatbotPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("pf_token");
      const res = await fetch(`${API}/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });

      if (res.status === 402) {
        const j = await res.json();
        setMessages((m) => m.slice(0, -1));
        toast.error(j.detail || "Free limit reached", {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/pricing") },
        });
        setStreaming(false);
        return;
      }
      if (!res.ok) throw new Error("Request failed");

      const sid = res.headers.get("X-Session-Id");
      if (sid) setSessionId(sid);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I hit an issue. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="border-b border-white/8 glass px-4 sm:px-8 py-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-indigo-500 glow-cyan">
          <Bot className="h-5 w-5 text-[#041016]" />
        </div>
        <div>
          <h1 className="font-display text-lg font-bold">AI Career Coach</h1>
          <p className="text-xs text-emerald-400 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online · Claude Sonnet 4.6
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {sessionId && (
            <button data-testid="delete-chat-btn" title="Delete this chat" onClick={async () => {
              if (!window.confirm("Delete this conversation from our servers?")) return;
              try { await api.delete(`/privacy/chat/${sessionId}`); setMessages([]); setSessionId(null); toast.success("Conversation deleted"); }
              catch { toast.error("Could not delete"); }
            }} className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-300 hover:border-rose-400/40 hover:text-rose-300">
              <Trash2 className="h-3.5 w-3.5" /> Delete chat</button>
          )}
          <Link to="/dashboard/privacy" data-testid="chat-privacy-link" className="text-xs text-slate-500 hover:text-cyan-300">Privacy & Data</Link>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        <div className="mx-auto max-w-3xl">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-500 glow-cyan">
                <Sparkles className="h-8 w-8 text-[#041016]" />
              </div>
              <h2 className="font-display text-2xl font-bold">How can I help your career today?</h2>
              <p className="mt-2 text-slate-400">Ask me anything about courses, colleges, or exams.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="chat-suggestion-chip" onClick={() => send(s)}
                    className="rounded-xl border border-white/10 bg-[#131C2E] px-4 py-3 text-left text-sm text-slate-300 transition-all hover:border-cyan-400/30 hover:text-white">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-6">
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                data-testid="chat-message-bubble" className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  m.role === "user" ? "bg-indigo-500/20 text-indigo-300" : "bg-cyan-400/15 text-cyan-300"
                }`}>
                  {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-indigo-500/15 text-white" : "bg-[#131C2E] border border-white/8 text-slate-200"
                }`}>
                  {m.role === "assistant" && m.content === "" && streaming ? (
                    <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />
                  ) : m.role === "assistant" ? (
                    <div className="prose-chat">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                      {streaming && i === messages.length - 1 && <span className="blink text-cyan-400">▋</span>}
                    </div>
                  ) : m.content}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-white/8 px-4 sm:px-8 py-4">
        <div className="mx-auto max-w-3xl flex items-end gap-3">
          <textarea data-testid="chat-input-field" rows={1} value={input} placeholder="Ask your career question..."
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="flex-1 resize-none rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20 max-h-32" />
          <button data-testid="chat-send-btn" onClick={() => send()} disabled={streaming || !input.trim()}
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-400 text-[#041016] transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100">
            {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
        <p className="mx-auto max-w-3xl mt-2 text-center text-xs text-slate-500">
          Free plan includes 5 AI messages. <Link to="/pricing" className="text-cyan-400 inline-flex items-center gap-1"><Crown className="h-3 w-3" /> Go unlimited</Link>
        </p>
      </div>
    </div>
  );
}
